import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Collapse } from "@mui/material";
import { useSnackbar } from "notistack";
import { SectionCard, Icon, EmptyState } from "../../../mui";
import { brainTokens } from "../../../design/brainTheme";
import {
  listLibrary,
  patchLibraryMetaApi,
  type LibraryDocRow,
  type LibraryViewer,
} from "../../../lib/brain-api";
import { LibraryRow } from "./LibraryRow";
import { LibraryDocView } from "./LibraryDocView";

const c = brainTokens.color;

function sectionTitle(scope: string, deptLabels: Record<string, string>): string {
  if (scope === "canon") return "Company canon";
  if (scope.startsWith("dept:")) return deptLabels[scope.slice(5)] ?? scope.slice(5);
  if (scope.startsWith("project:")) return scope.slice(8);
  return scope;
}

export function LibraryTab({
  scopeFilter,
  openDocKey,
  onOpenDoc,
  onCloseDoc,
  deptLabels = {},
}: {
  /** When set, only show this scope (dept page reuse). */
  scopeFilter?: string;
  openDocKey?: string | null;
  onOpenDoc: (key: string | null) => void;
  onCloseDoc?: () => void;
  deptLabels?: Record<string, string>;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [byScope, setByScope] = useState<Record<string, LibraryDocRow[]>>({});
  const [byTag, setByTag] = useState<Record<string, LibraryDocRow[]>>({});
  const [allTags, setAllTags] = useState<string[]>([]);
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"scope" | "tag">("scope");
  const [viewer, setViewer] = useState<LibraryViewer | null>(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [creating, setCreating] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listLibrary(scopeFilter, tagFilter ?? undefined);
      setByScope(data.byScope);
      setByTag(data.byTag ?? {});
      setAllTags(data.tags ?? []);
      setViewer(data.viewer ?? null);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to load library", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [scopeFilter, tagFilter, enqueueSnackbar]);

  useEffect(() => {
    void load();
  }, [load]);

  const scopes = useMemo(() => {
    const keys = Object.keys(byScope);
    const order = (s: string) => (s === "canon" ? 0 : s.startsWith("dept:") ? 1 : 2);
    return keys.sort((a, b) => order(a) - order(b) || a.localeCompare(b));
  }, [byScope]);

  const tagKeys = useMemo(() => Object.keys(byTag).sort(), [byTag]);

  if (openDocKey || creating) {
    return (
      <LibraryDocView
        docKey={openDocKey}
        createScope={creating ?? undefined}
        onBack={() => {
          setCreating(null);
          onOpenDoc(null);
          onCloseDoc?.();
          void load();
        }}
        onSaved={(key) => {
          setCreating(null);
          onOpenDoc(key);
          void load();
        }}
      />
    );
  }

  if (loading) return <Box sx={{ fontSize: 13, color: c.muted2 }}>Loading library…</Box>;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
        <Chip
          size="small"
          label="By scope"
          color={viewMode === "scope" ? "primary" : "default"}
          onClick={() => setViewMode("scope")}
          variant={viewMode === "scope" ? "filled" : "outlined"}
        />
        <Chip
          size="small"
          label="By tag"
          color={viewMode === "tag" ? "primary" : "default"}
          onClick={() => setViewMode("tag")}
          variant={viewMode === "tag" ? "filled" : "outlined"}
        />
        {allTags.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, ml: 1 }}>
            <Chip
              size="small"
              label="All tags"
              variant={tagFilter ? "outlined" : "filled"}
              onClick={() => setTagFilter(null)}
            />
            {allTags.map((t) => (
              <Chip
                key={t}
                size="small"
                label={t}
                variant={tagFilter === t ? "filled" : "outlined"}
                color={tagFilter === t ? "primary" : "default"}
                onClick={() => setTagFilter(tagFilter === t ? null : t)}
              />
            ))}
          </Box>
        )}
      </Box>

      {scopes.length === 0 && tagKeys.length === 0 && (
        <EmptyState icon="book" title="Library is empty">
          Curated docs will land here — start with a company canon overview.
          {viewer?.canCreate[scopeFilter || "canon"] && (
            <Box sx={{ mt: 1.5 }}>
              <Button variant="contained" size="small" onClick={() => setCreating(scopeFilter || "canon")}>
                New doc
              </Button>
            </Box>
          )}
        </EmptyState>
      )}

      {viewMode === "tag"
        ? tagKeys.map((tag) => {
            const rows = byTag[tag] ?? [];
            return (
              <SectionCard key={tag} label={`#${tag} (${rows.length})`}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {rows.map((d) => (
                    <LibraryRow
                      key={`${tag}:${d.docKey}`}
                      doc={d}
                      canEdit={Boolean(d.canEdit)}
                      onOpen={() => onOpenDoc(d.docKey)}
                    />
                  ))}
                </Box>
              </SectionCard>
            );
          })
        : scopes.map((scope) => {
            const rows = byScope[scope] ?? [];
            const pinned = rows.filter((d) => d.pinned);
            const rest = rows.filter((d) => !d.pinned);
            const isProject = scope.startsWith("project:");
            const isCollapsed = collapsed[scope] ?? isProject;
            return (
              <SectionCard
                key={scope}
                label={`${sectionTitle(scope, deptLabels)} (${rows.length})`}
                actions={
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setCollapsed((s) => ({ ...s, [scope]: !isCollapsed }))}
                    >
                      {isCollapsed ? "Expand" : "Collapse"}
                    </Button>
                    {viewer?.canCreate[scope] && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Icon name="plus" size={14} />}
                        onClick={() => setCreating(scope)}
                      >
                        New doc
                      </Button>
                    )}
                  </Box>
                }
              >
                <Collapse in={!isCollapsed}>
                  {rows.length === 0 ? (
                    <Box sx={{ fontSize: 13, color: c.muted2, py: 1 }}>
                      Nothing here yet — add a Start-here doc when you know what this team needs.
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {pinned.length > 0 && (
                        <Box sx={{ mb: 0.5 }}>
                          <Box
                            sx={{
                              fontFamily: brainTokens.font.mono,
                              fontSize: 10.5,
                              letterSpacing: "0.08em",
                              textTransform: "uppercase",
                              color: c.tealInk,
                              mb: 1,
                            }}
                          >
                            Start here
                          </Box>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {pinned.map((d) => (
                              <LibraryRow
                                key={d.docKey}
                                doc={d}
                                hero
                                canEdit={Boolean(d.canEdit)}
                                onOpen={() => onOpenDoc(d.docKey)}
                                onPin={async () => {
                                  await patchLibraryMetaApi({ key: d.docKey, pinned: false });
                                  void load();
                                }}
                                onArchive={async () => {
                                  await patchLibraryMetaApi({ key: d.docKey, status: "archived" });
                                  void load();
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                      {rest.map((d) => (
                        <LibraryRow
                          key={d.docKey}
                          doc={d}
                          canEdit={Boolean(d.canEdit)}
                          onOpen={() => onOpenDoc(d.docKey)}
                          onPin={async () => {
                            await patchLibraryMetaApi({ key: d.docKey, pinned: true });
                            void load();
                          }}
                          onArchive={async () => {
                            await patchLibraryMetaApi({ key: d.docKey, status: "archived" });
                            void load();
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Collapse>
              </SectionCard>
            );
          })}
    </Box>
  );
}
